import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Truck, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getDaysAgoAndColor } from '@/lib/getDaysAgoAndColor';
import { PackageData, carrierStyle, storageStyle, formatDate } from '@/lib/packageUtils';

interface PackageDetailsModalProps {
  isOpen: boolean;
  pkg: PackageData | null;
  actionButtonText: string;
  isEditing?: boolean;
  isPickedButton?: boolean;
  onClose: () => void;
  onActionClick: (e: React.MouseEvent) => void;
}

export const PackageDetailsModal = ({
  isOpen,
  pkg,
  actionButtonText,
  isEditing = false,
  isPickedButton = true,
  onClose,
  onActionClick,
}: PackageDetailsModalProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isImagePreviewOpen, setIsImagePreviewOpen] = React.useState(false);

  if (!isOpen || !pkg) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg border border-border shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto hide-scrollbar text-left">
        {/* Modal header */}
        <div className="flex items-center justify-between px-4 py-3 sticky top-0 bg-background z-10 border-b">
          <h2 className="text-xl font-semibold">{t('package_details')}</h2>
          <div className="flex items-center gap-2">
            {isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(
                    `/slot-result?packageId=${pkg.id}&packageNumber=${encodeURIComponent(pkg.packageNumber || '')}&from=${encodeURIComponent(location.pathname)}`,
                  );
                }}
                className="px-3 py-2 flex items-center gap-2 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:text-blue-800"
              >
                <Pencil className="w-4 h-4" />
                {t('edit')}
              </Button>
            )}
            {isPickedButton && (
              <Button variant="default" size="sm" onClick={onActionClick} className="px-3 py-2">
                {actionButtonText}
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-1 h-8 w-8 rounded-sm bg-gray-200 hover:bg-gray-100"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 gap-6">
          {/* Left column */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('package_number')}</p>
                <p className="text-2xl font-bold leading-none text-foreground calculator-font">
                  {pkg.packageNumber || t('na')}
                </p>
              </div>
              {pkg.packageMetadata?.carrier && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{t('shipping_provider')}</p>
                  <div
                    className={`flex items-center gap-2 px-2 py-0.5 rounded-full border w-fit ${carrierStyle(pkg.packageMetadata.carrier).bg} ${carrierStyle(pkg.packageMetadata.carrier).border} ${carrierStyle(pkg.packageMetadata.carrier).text}`}
                  >
                    <Truck className="w-4 h-4" />
                    <p className="overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold first-letter:uppercase">
                      {pkg.packageMetadata.carrier}
                    </p>
                  </div>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('frame_bin')}</p>
                <p className="flex items-center text-sm font-bold px-2 py-0.5 rounded-full bg-white border border-primary text-foreground w-fit">
                  {pkg.frameNumber || t('na')}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('box_number')}</p>
                <p className="flex items-center text-sm font-bold px-2 py-0.5 rounded-full bg-white border border-indigo-700 foreground w-fit">
                  {pkg.boxNumber || t('na')}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('storage_type')}</p>
                <span
                  className={`flex items-center text-sm font-bold px-2 py-0.5 rounded-full border w-fit ${storageStyle(pkg.storageOption).bg} ${storageStyle(pkg.storageOption).border} ${storageStyle(pkg.storageOption).text}`}
                >
                  {pkg.storageOption ? t(pkg.storageOption.toLowerCase()) : t('na')}
                </span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('receiver_name')}</p>
                <span className="font-medium">{pkg.packageMetadata?.receiver?.name || t('na')}</span>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">{t('weight')}</p>
                <p className="font-medium">
                  {pkg.packageMetadata?.weight?.value || t('na')}{' '}
                  {pkg.packageMetadata?.weight?.value ? pkg.packageMetadata?.weight?.unit : ''}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('storage_date')}</p>
              <p className={`text-md ${getDaysAgoAndColor(pkg.createdAt, t).colorClass}`}>
                {formatDate(pkg.createdAt)}
                {getDaysAgoAndColor(pkg.createdAt, t).message && (
                  <span className={`text-sm ml-1 ${getDaysAgoAndColor(pkg.createdAt, t).colorClass}`}>
                    ({getDaysAgoAndColor(pkg.createdAt, t).message})
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('notes')}</p>
              <p className="uppercase text-md font-medium text-foreground/80">
                {pkg.packageMetadata?.customNote || t('na')}
              </p>
            </div>
          </div>
          <div className="border-t border-border/80 mb-2" />
          {/* Right column */}
          <div className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground mb-1">{t('barcodes')}</p>
              <div className="bg-muted p-3 rounded space-y-1">
                {pkg.barcodes?.length ? (
                  pkg.barcodes.map((b, i) => (
                    <p key={i} className="font-mono text-sm">
                      {b}
                    </p>
                  ))
                ) : (
                  <p className="text-sm">{t('na')}</p>
                )}
              </div>
            </div>

            {[
              { label: t('receiver_information'), person: pkg.packageMetadata?.receiver },
              { label: t('sender_information'), person: pkg.packageMetadata?.sender },
            ].map(({ label, person }) => (
              <div key={label}>
                <p className="text-sm text-muted-foreground mb-1">{label}</p>
                <div className="bg-muted p-3 rounded space-y-1">
                  {person?.name ? (
                    <div>
                      <p className="font-medium">{person?.name}</p>
                      <p className="text-sm">{person?.address}</p>
                      <p className="text-sm">
                        {person?.city}, {person?.country} {person?.postalCode}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm">{t('na')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {pkg.imageUrl && (
          <div className="px-6 pb-6">
            <p className="text-sm text-muted-foreground mb-2">{t('package_image')}</p>
            <div className="w-full rounded bg-muted flex items-center justify-center overflow-hidden">
              <img
                src={pkg.imageUrl}
                alt={t('package')}
                onClick={() => setIsImagePreviewOpen(true)}
                className="max-w-full max-h-[600px] object-contain cursor-zoom-in"
              />
            </div>
          </div>
        )}
      </div>

      {isImagePreviewOpen && pkg.imageUrl && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => setIsImagePreviewOpen(false)}
        >
          <button
            type="button"
            onClick={() => setIsImagePreviewOpen(false)}
            className="absolute top-4 right-4 text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={pkg.imageUrl}
            alt={t('package')}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full h-full object-contain"
          />
        </div>
      )}
    </div>
  );
};
